/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import RepeatIcon from "@mui/icons-material/Repeat";
import IconButton from "@mui/material/IconButton";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DetailsPopup from "../DetailsPopUp/DetailsPopup";
import TaxTablePopUp from "../TaxTablePopUp/TaxTablePopUp";

const monthNames = [
  "Jan",
  "Feb",
  "March",
  "Apr",
  "May",
  "June",
  "July",
  "August",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

const headCells = [
  { id: "productId", numeric: false, disablePadding: false, label: "Product Id" },
  { id: "price", numeric: false, disablePadding: false, label: "Price" },
  { id: "trialPeriod", numeric: false, disablePadding: false, label: "Trial Period" },
  { id: "noOfPlans", numeric: true, disablePadding: false, label: "No. Of Plans" },
  { id: "lastModified", numeric: true, disablePadding: false, label: "Last Modified" },
];

const headCellsPremium = [
  { id: "planName", numeric: false, disablePadding: false, label: "Plan Name" },
  { id: "price", numeric: false, disablePadding: false, label: "Base Price" },
  { id: "offers", numeric: false, disablePadding: false, label: "Offers" },
  { id: "lastModified", numeric: true, disablePadding: false, label: "Last Modified" },
];

function descendingComparator(a, b, orderByField) {
  if (b[orderByField] < a[orderByField]) {
    return -1;
  }
  if (b[orderByField] > a[orderByField]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderByField) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderByField)
    : (a, b) => -descendingComparator(a, b, orderByField);
}

function EnhancedTableHead({ order, orderBy, onRequestSort, listType }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  const headerCells = listType === "App" ? headCellsPremium : headCells;

  return (
    <TableHead className="tableHeaderCls">
      <TableRow
        sx={{
          backgroundColor: "#f3f3f3 !important",
          border: "1px solid #f3f3f3 !important",
          height: "32px",
          '& .MuiTableCell-root': {
            paddingTop: "4px",
            paddingBottom: "4px",
            height: "32px"
          }
        }}
      >
        {headerCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ 
              fontWeight: "bold",
              paddingTop: "4px",
              paddingBottom: "4px",
              height: "32px"
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc"
                    ? "sorted descending"
                    : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  listType: PropTypes.string.isRequired,
};

function Row({ row, onSelectProduct, onOpenDetails, onSetTaxType, onOpenTaxDetails }) {
  return (
    <TableRow
      hover
      tabIndex={-1}
      sx={{ 
        cursor: "pointer",
        height: "34px",
        '& .MuiTableCell-root': {
          paddingTop: "4px",
          paddingBottom: "4px",
          height: "34px"
        }
      }}
    >
      <TableCell
        component="th"
        scope="row"
        padding="normal"
        align="left"
        sx={{ 
          fontWeight: "bold", 
          width: "20%", 
          paddingTop: "4px", 
          paddingBottom: "4px",
          height: "34px"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {row.productId.length > 15
            ? `${row.productId.substring(0, 15)}...`
            : row.productId}
          {row.type === "recurring" && <RepeatIcon sx={{ fontSize: 20 }} />}
        </div>
      </TableCell>
      <TableCell align="left" sx={{ width: "15%", paddingTop: "4px", paddingBottom: "4px", height: "34px" }}>
        <span style={{ color: "#717273" }}>
          {row.type === "oneTime" && "USD"}
        </span>{" "}
        {row.price}
      </TableCell>
      <TableCell align="left" sx={{ width: "17%", paddingTop: "4px", paddingBottom: "4px", height: "34px" }}>
        {row.trialPeriod}
      </TableCell>
      <TableCell align="left" sx={{ width: "19%", paddingTop: "4px", paddingBottom: "4px", height: "34px" }}>
        {row.noOfPlans}
      </TableCell>
      <TableCell align="left" sx={{ paddingTop: "4px", paddingBottom: "4px", height: "34px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{`${new Date(row.lastModified).getDate()} ${
            monthNames[new Date(row.lastModified).getMonth()]
          } ${new Date(row.lastModified).getFullYear()}`}</span>
          <span className="icons-cont">
            {row.type === "recurring" && (
              <IconButton
                aria-label="expand row"
                size="small"
                sx={{ transform: "scale(.8)" }}
                onClick={() => {
                  onSelectProduct(row);
                  onOpenDetails();
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            )}
            {row.type === "oneTime" && (
              <IconButton
                aria-label="expand row"
                size="small"
                sx={{ transform: "scale(.8)" }}
                onClick={() => {
                  onSetTaxType("InApp");
                  onSelectProduct(row);
                  onOpenTaxDetails();
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            )}
          </span>
        </div>
      </TableCell>
    </TableRow>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productId: PropTypes.string,
    type: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trialPeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    noOfPlans: PropTypes.number,
    lastModified: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onSelectProduct: PropTypes.func.isRequired,
  onOpenDetails: PropTypes.func.isRequired,
  onSetTaxType: PropTypes.func.isRequired,
  onOpenTaxDetails: PropTypes.func.isRequired,
};

function RowPremium({ row, onSelectProduct, onSetTaxType, onOpenTaxDetails }) {
  return (
    <TableRow hover tabIndex={-1} sx={{ 
      cursor: "pointer",
      height: "34px",
      '& .MuiTableCell-root': {
        paddingTop: "4px",
        paddingBottom: "4px",
        height: "34px"
      }
    }}>
      <TableCell
        component="th"
        scope="row"
        align="left"
        sx={{ 
          fontWeight: "bold",
          paddingTop: "4px",
          paddingBottom: "4px",
          height: "34px",
          display: "flex",
          alignItems: "center"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {row.name}
          {row.type === "RECURRING" && <RepeatIcon sx={{ fontSize: 20 }} />}
        </div>
      </TableCell>
      <TableCell align="left" sx={{ paddingTop: "4px", paddingBottom: "4px", height: "34px" }}>
        <span style={{ color: "#717273" }}>{row.currencyCode}</span> {row.price.toFixed(2)}
      </TableCell>
      <TableCell align="left" sx={{ paddingTop: "4px", paddingBottom: "4px", height: "34px" }}>0</TableCell>
      <TableCell align="left" sx={{ paddingTop: "4px", paddingBottom: "4px", height: "34px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{`${new Date(row.lastUpdated).getDate()} ${
            monthNames[new Date(row.lastUpdated).getMonth()]
          } ${new Date(row.lastUpdated).getFullYear()}`}</span>
          <span className="icons-cont">
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => {
                if (row.type === "RECURRING") {
                  onSetTaxType("PremiumRec");
                } else {
                  onSetTaxType("PremiumOne");
                }
                onSelectProduct(row);
                onOpenTaxDetails();
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </span>
        </div>
      </TableCell>
    </TableRow>
  );
}

RowPremium.propTypes = {
  row: PropTypes.shape({
    planId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    type: PropTypes.string,
    price: PropTypes.number,
    currencyCode: PropTypes.string,
    lastUpdated: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onSelectProduct: PropTypes.func.isRequired,
  onSetTaxType: PropTypes.func.isRequired,
  onOpenTaxDetails: PropTypes.func.isRequired,
};

function PricingTable(props) {
  const { listType, data } = props;

  useEffect(() => {
    setRows(data);
    handleRequestSort({}, "lastModified");
  }, [data]);

  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [showTaxDetailsModal, setShowTaxDetailsModal] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState("");
  const [taxModalType, setTaxModalType] = React.useState("");

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: "4px",
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage]
  );

  const openDetailsModal = () => {
    setShowDetailsModal(true);
  };

  const handleCloseDetailsPopup = () => {
    setShowDetailsModal(false);
  };

  const openTaxDetailsModal = () => {
    setShowTaxDetailsModal(true);
  };

  const handleCloseTaxDetailsPopup = () => {
    setShowTaxDetailsModal(false);
  };

  return (
    <Box sx={{ width: "100%", marginTop: "2%" }}>
      <Paper sx={{ width: "100%", mb: 2 }} elevation={0}>
        <TableContainer component={Paper} elevation={0}>
          <Table aria-label="collapsible table" sx={{ borderRadius: "5px" }}>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              listType={listType}
            />
            <TableBody>
              {visibleRows.map((row) => (
                <React.Fragment key={row.id}>
                  {listType === "InApp" && (
                    <Row
                      row={row}
                      onSelectProduct={setSelectedProduct}
                      onOpenDetails={openDetailsModal}
                      onSetTaxType={setTaxModalType}
                      onOpenTaxDetails={openTaxDetailsModal}
                    />
                  )}
                  {listType === "App" && (
                    <RowPremium
                      row={row}
                      onSelectProduct={setSelectedProduct}
                      onSetTaxType={setTaxModalType}
                      onOpenTaxDetails={openTaxDetailsModal}
                    />
                  )}
                </React.Fragment>
              ))}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Items per page"
        />
      </Paper>
      <Modal
        open={showDetailsModal}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box sx={{ ...style, width: 750 }}>
          <DetailsPopup
            onClose={handleCloseDetailsPopup}
            productData={selectedProduct}
          />
        </Box>
      </Modal>
      <Modal
        open={showTaxDetailsModal}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box sx={{ ...style, width: 750, maxHeight: 500, overflow: "scroll" }}>
          <TaxTablePopUp
            onClose={handleCloseTaxDetailsPopup}
            productData={selectedProduct}
            type={taxModalType}
          />
        </Box>
      </Modal>
    </Box>
  );
}

PricingTable.propTypes = {
  listType: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
};

export default PricingTable;
